import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";
import { CommonService } from "./common.service";
import { HttpHeaders } from "@angular/common/http";
import { BindingFlags } from "@angular/compiler/src/core";
import { EventEmitter, Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { Observable, of } from "rxjs";
import { CacheService } from "./cache.service";
import { TranslateService } from "@ngx-translate/core";
import { DIMBO_CONSTANT } from "./../utils/constant";
import { error } from "protractor";
import localeNb from "@angular/common/locales/nb";
import { registerLocaleData } from "@angular/common";
import { BrandcareFormService } from "./brandcare-form.service";
import { AppConfig } from "../app.config";
registerLocaleData(localeNb, "nb");

/** Auth service: validates token, manages cache/session, localization, logout. */
@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  public tokenValidationError: EventEmitter<string> =
    new EventEmitter<string>();

  constructor(
    private router: Router,
    private cacheService: CacheService,
    private translationService: TranslateService,
    private toastrService: ToastrService,
    private commonService: CommonService,
    private brandcareFormService: BrandcareFormService,
    private appConfig: AppConfig,
  ) {}

  // Method to handle to athentication failure of the user
  public unauthenticateUser() {
    this.cacheService.clear();
    this.brandcareFormService.clearCache();
    this.router.navigate(["/"]);
  }

  /**
   * Validates the provided authentication token.
   *
   * This method clears the cache before attempting to validate the token.
   * If the token is valid, it resolves to an observable emitting `true`.
   * If the token is invalid or an error occurs during validation, it rejects with `false` or the error.
   * If no token is provided, the user is unauthenticated, and the method rejects with `false`.
   *
   * @param token - The authentication token to validate.
   * @returns A promise that resolves to an observable emitting `true` if the token is valid,
   *          or rejects with `false` or an error if the token is invalid or an error occurs.
   */
  async validateToken(token: string): Promise<Observable<boolean>> {
    this.cacheService.clear();
    if (token) {
      try {
        let isUserValid = <boolean>await this.authenticateByToken(token);
        if (isUserValid) {
          return of(true);
        } else {
          return Promise.reject(false);
        }
      } catch (error) {
        return Promise.reject(error);
      }
    } else {
      this.unauthenticateUser();
      return Promise.reject(false);
    }
  }

  // Method to call the authentication API based on Token
  private authenticateByToken(token: string): Promise<boolean> {
    let flag: boolean = false;
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        this.commonService.authenticateToken(token).subscribe(
          (response) => {
            if (response) {
              this.cacheService.clearDmbrSessionData();
              this.brandcareFormService.clearCache();
              flag = true;

              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_BRANDCARE_TITLE,
                response["programTitle"],
              );

              /*Hub flow*/
              this.cacheService.setItem(DIMBO_CONSTANT.CACHE_JWT_TOKEN, token);
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_TOKEN_RESPONSE,
                response,
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_ISS,
                response["iss"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_LANGUAGE,
                this.getLanguage(response["locale"]),
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_LANGUAGE_RAW,
                response["language"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_LOCALE,
                response["locale"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_USER_ID,
                response["userID"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_RRDI,
                response["rrdi"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_MODEL,
                response["model"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_VIN_BRAND,
                response["vinBrand"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_VIN_RRDI,
                response["vinRrdi"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_ENGINE_TYPE,
                response["engineType"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_VEHICLE_TYPE,
                response["vehicleType"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_WARRANTY_START_DATE,
                response["warrantyStartDate"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_CONTRIBUTION_FLAG,
                response["contributionFlag"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_VIN,
                response["vin"],
              );

              // Adding new parameters for Panier flow
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_MILEAGE,
                response["mileage"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_RECEPTION_RRDI,
                response["receptionRrdi"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_FIRST_REGISTRATION_DATE,
                response["firstRegistrationDate"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_EMAIL,
                response["email"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_WORK_LINES,
                response["workLines"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_FOLDER_PNR_ID,
                response["folderPnrId"],
              );
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_JWT_FOLDER_DMS_ID,
                response["folderDmsId"],
              );
	      this.cacheService.setItem(DIMBO_CONSTANT.CACHE_JWT_PHSYSICAL_SITE, response['physicalSite']); 

              let userId: string = response["userID"];
              let rrdi: string = response["rrdi"];
              const iss: string = (response["iss"] || "").toUpperCase();

              if (iss === "HUB" && userId === rrdi) {
                this.cacheService.setItem(
                  DIMBO_CONSTANT.CACHE_IS_CENTRAL_USER,
                  true,
                );
              } else {
                this.cacheService.setItem(
                  DIMBO_CONSTANT.CACHE_IS_CENTRAL_USER,
                  false,
                );
              }
            }
            resolve(flag);
          },
          (error) => {
            if (
              error &&
              error.status &&
              [400, 401, 403, 503].includes(error.status) &&
              error.error
            ) {
              this.cacheService.setItem(
                DIMBO_CONSTANT.CACHE_BRANDCARE_TITLE,
                DIMBO_CONSTANT.BRAND_CARE_TITLE,
              );

              let tokenLang = "en";
              try {
                const decoded: any = token
                  ? JSON.parse(atob(token.split(".")[1]))
                  : null;
                tokenLang = decoded?.language || decoded?.locale || "en";
              } catch (e) {
                tokenLang = "en";
              }

              this.translationService.use(tokenLang);

              this.translationService.use(tokenLang).subscribe(() => {
                let errorMsg = this.translationService.instant(
                  error.error.message,
                );
                // If translation not found, fallback to English
                if (errorMsg === error.error.message) {
                  this.translationService.use("en").subscribe(() => {
                    errorMsg = this.translationService.instant(
                      error.error.message,
                    );
                    this.tokenValidationError.emit(errorMsg);
                  });
                } else {
                  this.tokenValidationError.emit(errorMsg);
                }
              });
            }
            this.unauthenticateUser();

            reject(error);
          },
        );
      }, 500);
    });
  }

  /**
   * Logs the user out by redirecting to the IDP logout URL.
   * Constructs the URL using `logoutURL` and `redirectURI` from the app config.
   */
  logoutToIdp() {
    const config = this.appConfig.getAppConfig();
    const logoutUrl = `${config.logoutURL}?TargetResource=${encodeURIComponent(config.redirectURI)}`;
    window.location.href = logoutUrl;
  }

  //DMBR-47 : Modify method for "no-NO" language - took 'nb' instead of 'no' as per new i18 guideline.
  public getLanguage(language: string) {
    if (language.length > 2) {
      const languageAr = language.split("_");
      if (languageAr[0] === "no") {
        languageAr[0] = "nb";
      }
      return languageAr[0];
    } else {
      return language;
    }
  }
}
